import { useState } from "react";
import { useTranslation } from "react-i18next";
import FormGroupTile from "../../basic/FormGroupTile";
import useAdPlanState from "./useAdPlanState";
import { useUpdateAdPlan } from "./useUpdateAdPlan";
import { Button } from "../../basic/Button";
import { Checkbox } from "../../../fields/base/Checkbox";
import { VStack, Wrap } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { useAppSelector } from "../../../../redux/hooks";

const AdPlanNotificationMenu = ({ adId, roas, categoryId, readOnly }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const updateAdPlan = useUpdateAdPlan({ adId });
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);
  const { adPlan, onChange } = useAdPlanState({}, marketPlace, categoryId, adId, roas, user.organization_type);
  const [errors, setErrors] = useState([]);

  const onSubmit = async () => {
    if (errors.length > 0) {
      return;
    }
    updateAdPlan.mutate({ adPlan });
  };

  return (
    <VStack gap="xlarge">
      <h2 className={css({ margin: 0, fontSize: "xlarge", fontWeight: "bold", color: "text.heading" })}>
        {t(($) => $.advertising["Manage Media Plan Notifications"])}
      </h2>

      <Wrap alignSelf={"stretch"} justify="space-evenly" paddingX="xlarge" alignItems="end">
        <Checkbox
          label={t(($) => $.advertising["Product Alert"])}
          value={adPlan.notifications?.productAlertEnabled}
          onChange={() => {
            onChange({
              notifications: {
                ...adPlan.notifications,
                productAlertEnabled: !adPlan.notifications?.productAlertEnabled,
              },
            });
          }}
          isDisabled={readOnly}
        />

        <FormGroupTile
          type="formcontrol"
          label={
            roas ?
              t(($) => $.advertising["Optimal ROAS Range Low"])
            : t(($) => $.advertising["Optimal ACOS Range High"])
          }
          formControlType={"number"}
          prefix={"%"}
          prefixSide={"right"}
          toolTip={
            roas ?
              t(($) => $.advertising["Short term ad spend as a rating of sales to enable successful campaign launch."])
            : t(
                ($) =>
                  $.advertising[
                    "ACoS is a metric that measures how much you spend on an advertisement for every dollar of revenue you make. The max ACoS is the maximum amount of money you want to spend on an advertisement per dollar of revenue made."
                  ],
              )
          }
          stateName={roas ? "minROAS" : "maxACOS"}
          handleChildFormElement={(key, value) => {
            onChange({
              notifications: { ...adPlan.notifications, maxAcos: value },
            });
          }}
          placeholder={roas ? t(($) => $.advertising["Enter Min ROAS"]) : t(($) => $.advertising["Enter Max ACoS (%)"])}
          defaultValue={
            roas ?
              // TODO: figure out how values for ROAS will be passed to backend => do math here when setting state, or just take care of it in serializer/view once passed
              adPlan.notifications.minROAS
            : adPlan?.notifications?.maxAcos
          }
          disabled={readOnly}
        />

        <FormGroupTile
          type="formcontrol"
          label={t(($) => $.advertising["Max Spend"])}
          formControlType={"number"}
          toolTip={t(
            ($) =>
              $.advertising[
                "Enter a limit to the amount spent on an advertised product that, if exceeded, will trigger an alert."
              ],
          )}
          stateName={"maxSpend"}
          handleChildFormElement={(key, value) => {
            if (!Number.parseInt(value) || Number.parseInt(value) < 0) {
              setErrors([...errors, t(($) => $.advertising["Please enter a positive integer."])]);
            } else {
              setErrors([]);
            }
            onChange({
              notifications: { ...adPlan.notifications, maxSpend: value },
            });
          }}
          placeholder={t(($) => $.advertising["Enter Max Spend"])}
          defaultValue={adPlan?.notifications?.maxSpend}
          disabled={readOnly}
        />
      </Wrap>
      <Button onPress={onSubmit} isDisabled={readOnly}>
        {t(($) => $.advertising["Update"])}
      </Button>
    </VStack>
  );
};

export default AdPlanNotificationMenu;
